/**
 * Referral System Utilities
 *
 * Helper functions for managing referral codes, URL parameters, and localStorage.
 */

const PENDING_REFERRAL_KEY = 'junk_fun_pending_referral';
const INVITE_CODE_PATTERN = /^[A-Z0-9]{5}$/;

/**
 * Parse referral code from URL query parameters
 * Looks for ?ref= or ?referral= parameter
 *
 * @returns Uppercase referral code or null if not found
 */
export function parseReferralCodeFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('ref') || params.get('referral');
  return code ? code.toUpperCase() : null;
}

/**
 * Validate invite code format
 * Must be exactly 5 uppercase alphanumeric characters
 *
 * @param code - Code to validate
 * @returns True if code matches expected format
 */
export function isValidInviteCodeFormat(code: string): boolean {
  return INVITE_CODE_PATTERN.test(code);
}

/**
 * Store pending referral code in localStorage
 * Only stores if code passes format validation
 *
 * @param code - Referral code to store
 */
export function storePendingReferralCode(code: string): void {
  if (isValidInviteCodeFormat(code)) {
    localStorage.setItem(PENDING_REFERRAL_KEY, code);
  }
}

/**
 * Get pending referral code from localStorage
 *
 * @returns Stored referral code or null if none exists
 */
export function getPendingReferralCode(): string | null {
  return localStorage.getItem(PENDING_REFERRAL_KEY);
}

/**
 * Clear pending referral code from localStorage
 * Call after successful binding or on error
 */
export function clearPendingReferralCode(): void {
  localStorage.removeItem(PENDING_REFERRAL_KEY);
}
